// Pure pieces of the receipts browser and the summary strip: date presets,
// the list query from the view's filters, paging, the summary split, and the
// one-line headline each dashboard section shows when it is collapsed.

import { usd as money } from './money';
import { isPayment } from './receiptsModel';

const pad = (n) => String(n).padStart(2, '0');
const ymd = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;

export const DATE_PRESETS = [
  { key: 'month', label: 'This month' },
  { key: 'last', label: 'Last month' },
  { key: 'quarter', label: 'This quarter' },
  { key: 'ytd', label: 'Year to date' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom' },
];

/** { from, to } (YYYY-MM-DD, or null for open) for a preset; custom uses the given dates. */
export function presetRange(preset, today = new Date(), custom = {}) {
  const y = today.getFullYear(); const m = today.getMonth();
  if (preset === 'last') return { from: ymd(new Date(y, m - 1, 1)), to: ymd(new Date(y, m, 0)) };
  if (preset === 'quarter') { const q = Math.floor(m / 3) * 3; return { from: ymd(new Date(y, q, 1)), to: ymd(new Date(y, q + 3, 0)) }; }
  if (preset === 'ytd') return { from: `${y}-01-01`, to: ymd(today) };
  if (preset === 'all') return { from: null, to: null };
  if (preset === 'custom') return { from: custom.from || null, to: custom.to || null };
  return { from: ymd(new Date(y, m, 1)), to: ymd(new Date(y, m + 1, 0)) };
}

export const MATCH_FILTERS = [
  { key: 'matched', label: 'Matched' },
  { key: 'noBank', label: 'No bank match expected' },
  { key: 'waiting', label: 'Waiting' },
  { key: 'notFound', label: 'No charge found' },
];
export const CONTEXT_FILTERS = [
  { key: 'FIG', label: 'FIG' }, { key: 'Personal', label: 'Personal' }, { key: 'Monduras', label: 'Monduras' },
  { key: 'Split', label: 'Split' }, { key: 'unconfirmed', label: 'Not confirmed' },
];
export const SORTS = [{ key: 'date', label: 'Date' }, { key: 'amount', label: 'Amount' }, { key: 'merchant', label: 'Merchant' }];

export const PAGE_SIZE = 50;

/** The browser's starting state; `over` lets the dashboard hand over its range and person. */
export function defaultBrowse(over = {}) {
  return { preset: 'month', from: '', to: '', person: null, context: '', merchant: '', match: '', flagged: false, owed: false, q: '', sort: 'date', dir: 'desc', ...over };
}

/** The list call for a state and an offset. Lines are never asked for here. */
export function listQuery(state, offset = 0, today = new Date()) {
  const r = presetRange(state.preset, today, { from: state.from, to: state.to });
  const p = { limit: PAGE_SIZE, offset, sort: state.sort || 'date', dir: state.dir || 'desc' };
  if (r.from) p.from = r.from;
  if (r.to) p.to = r.to;
  if (state.person) p.person = state.person;
  if (state.context) p.context = state.context;
  if (state.merchant && state.merchant.trim()) p.merchant = state.merchant.trim();
  if (state.match) p.match = state.match;
  if (state.flagged) p.flagged = true;
  if (state.owed) p.owed = true;
  if (state.q && state.q.trim()) p.q = state.q.trim();
  return p;
}

/** Add a page to what is shown, never twice the same receipt. -> { receipts, more } */
export function mergePage(shown, page) {
  const seen = new Set(shown.map((r) => r.id));
  const receipts = [...shown, ...(page.receipts || []).filter((r) => !seen.has(r.id))];
  return { receipts, more: receipts.length < (page.total ?? receipts.length) };
}

const CONFIRMED = new Set(['rcpt', 'loose', 'human']);
const cents = (d) => (typeof d === 'number' && Number.isFinite(d) ? Math.round(d * 100) : 0);

/**
 * The summary strip for receipts already in hand (the dashboard), the same
 * shape the server returns for the browser: count, total with tax, by
 * context — FIG / Personal / other, each only as a person chose it — and the
 * unconfirmed apart (over everything), purchases and payments apart, and tax
 * over purchases only: a payment is never "no tax line".
 */
export function summaryOf(receipts) {
  const b = { FIG: [0, 0], Personal: [0, 0], other: [0, 0], unconfirmed: [0, 0] };
  let total = 0; let missing = 0;
  const split = { purchases: [0, 0], payments: [0, 0] };
  for (const r of receipts || []) {
    total += cents(r.total);
    const s = split[isPayment(r) ? 'payments' : 'purchases']; s[0]++; s[1] += cents(r.total);
    if (typeof r.total !== 'number') missing++;
    const confirmed = CONFIRMED.has(r.tagSource) && r.context;
    const k = !confirmed ? 'unconfirmed' : r.context === 'FIG' ? 'FIG' : r.context === 'Personal' ? 'Personal' : 'other';
    b[k][0]++; b[k][1] += cents(r.total);
  }
  const part = ([n, c]) => ({ receipts: n, total: c / 100 });
  // Tax as the server counts it: total and tax both read; a tip only where
  // printed, and shown as tips so the figures add up to the total.
  let items = 0; let tax = 0; let taxed = 0; let noTaxN = 0; let noTaxC = 0; let tips = 0; let tipsN = 0;
  const num = (v) => typeof v === 'number' && Number.isFinite(v);
  for (const r of receipts || []) {
    if (!num(r.total) || isPayment(r)) continue;
    if (!num(r.tax)) { noTaxN++; noTaxC += cents(r.total); continue; }
    items += cents(r.total) - cents(r.tax) - (num(r.tip) ? cents(r.tip) : 0); tax += cents(r.tax); taxed++;
    if (num(r.tip)) { tips += cents(r.tip); tipsN++; }
  }
  return {
    receipts: (receipts || []).length, total: total / 100, receiptsWithoutTotal: missing,
    tax: { itemsBeforeTax: items / 100, tax: tax / 100, receipts: taxed, rate: items > 0 ? Math.round((tax / items) * 10000) / 10000 : null, tips: { receipts: tipsN, total: tips / 100 }, noTaxLine: { receipts: noTaxN, total: noTaxC / 100 } },
    byContext: { FIG: part(b.FIG), Personal: part(b.Personal), other: part(b.other), unconfirmed: part(b.unconfirmed) },
    purchases: part(split.purchases), payments: part(split.payments),
  };
}

const usd = (n) => `$${(Math.round(n * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * The one line a collapsed dashboard section shows.
 * @param parts { band, kinds, rj, who, where, items, receipts, months } — the model outputs
 */
export function sectionHeadlines({ band, kinds, rj, who, where, items, receipts, months, tax = null, kindLabels = {}, payments = null }) {
  const seg = (k) => band?.segments?.find((s) => s.key === k);
  const label = (k) => kindLabels[k] || k;
  const out = {};
  if (band) out.chase = `${seg('matched')?.receipts ?? 0} of ${receipts} matched · ${usd(seg('matched')?.amount ?? 0)}`;
  if (kinds?.rows?.length) out.kinds = `${label(kinds.rows[0].kind)} ${usd(kinds.rows[0].amount)} leads`;
  if (rj && (rj.real || rj.junk)) out.realJunk = `Real food ${Math.round(rj.realShare * 100)}% · ${(kindLabels.junk_food || 'junk').toLowerCase()} ${usd(rj.junk)}`;
  if (payments?.length) out.payments = `${payments.length} payment${payments.length === 1 ? '' : 's'} · ${usd(payments.reduce((a, p) => a + (typeof p.amount === 'number' ? p.amount : 0), 0))}`;
  if (who?.length) out.who = who.map((p) => `${p.person} ${usd(p.amount)}`).join(' / ');
  if (where?.length) out.where = `${where[0].merchant} ${usd(where[0].amount)}${where.length > 1 ? ` · ${where.length} stores` : ''}`;
  if (items?.length) out.items = `${items[0].name} ${usd(items[0].amount)}`;
  out.receipts = `${receipts} receipt${receipts === 1 ? '' : 's'}`;
  if (tax) out.tax = tax.receipts ? `${usd(tax.tax)} · ${(Math.round((tax.rate ?? 0) * 1000) / 10).toFixed(1)}% of items` : 'no tax lines read';
  if (months?.length) out.months = months.length === 1 ? `${usd(months[0].amount)} in one month` : `${months.length} months · latest ${usd(months[months.length - 1].amount)}`;
  return out;
}

const pct = (rate) => `${(Math.round(rate * 1000) / 10).toFixed(1)}%`;

/**
 * The Tax section's first sentence, from taxBreakdown: items + tax (+ tips,
 * only when the range has any) over the receipts with a tax line.
 */
export function taxSentence(tx) {
  return `Items ${money(tx.items)} + tax ${money(tx.tax)}${tx.rate !== null ? ` (${pct(tx.rate)})` : ''}${tx.tips?.receipts ? ` + tips ${money(tx.tips.total)}` : ''} on ${tx.receipts} receipt${tx.receipts === 1 ? '' : 's'} with a tax line.`;
}

/** The dashboard footer's sum, from reconcile and taxBreakdown: items (− savings) + tax (+ tips) = on receipts. */
export function footSum(rec, tx) {
  return `Items ${money(rec.items)}${tx.noTaxLine.receipts ? ` (${money(tx.noTaxLine.total)} of it on receipts with no tax line)` : ''}${rec.unallocated ? ` − savings ${money(rec.unallocated)}` : ''} + tax ${money(rec.tax)}${rec.tip ? ` + tips ${money(rec.tip)}` : ''} = ${money(rec.sum)} on receipts`;
}
